import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { DotsThreeVertical, Trash, Lock, LockOpen, PushPin, ArrowCounterClockwise, Flag, GitMerge } from "@phosphor-icons/react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "./ConfirmDialog";

export default function ModMenu({ targetType, targetId, isLocked, isPinned, isDeleted, onChange, onReport }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    // Recompute the portal-mounted menu's screen position whenever it opens
    // or the user scrolls/resizes. Keeps the dropdown glued to the trigger.
    useEffect(() => {
        if (!open) return;
        const reposition = () => {
            if (!btnRef.current) return;
            const r = btnRef.current.getBoundingClientRect();
            // Anchor right edge of menu to right edge of button, 4px below button.
            const MENU_WIDTH = 200;
            const left = Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
            setPos({ top: r.bottom + 4, left });
        };
        reposition();
        const onClick = (e) => {
            if (btnRef.current?.contains(e.target)) return;
            if (menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        window.addEventListener("scroll", reposition, true);
        window.addEventListener("resize", reposition);
        return () => {
            document.removeEventListener("mousedown", onClick);
            window.removeEventListener("scroll", reposition, true);
            window.removeEventListener("resize", reposition);
        };
    }, [open]);

    // Signed-out visitors still get an obvious "report abuse" path (HC-001 gap).
    if (!user) {
        return (
            <a href={`/login?next=${typeof window !== "undefined" ? encodeURIComponent(window.location.pathname) : ""}`}
               onClick={(e) => { e.stopPropagation(); toast.info("Sign in to report content — takes 20 seconds"); }}
               title="Report abuse — sign in required"
               data-testid={`report-signedout-${targetId}`}
               className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono text-slate-500 hover:text-rose-300 px-1.5 py-1 rounded-sm transition-colors">
                <Flag size={11} /> report
            </a>
        );
    }
    const isAdmin = user.role === "admin" || user.role === "mod";
    const canLockPin = isAdmin && (targetType === "question" || targetType === "post");
    const canMerge = isAdmin && targetType === "question";

    const merge = async () => {
        const target = await promptDialog("Merge INTO which question id? (the canonical one whose URL survives)", { title: "Merge question", confirmText: "merge", placeholder: "question id" });
        if (!target) return;
        const tgt = target.trim();
        if (tgt === targetId) { toast.error("Can't merge into itself"); return; }
        if (!await confirmDialog(`Move all answers + comments from this question to ${tgt}? Source URL stays alive as a redirect.`)) return;
        try {
            const r = await api.post(`/admin/questions/${targetId}/merge-into/${tgt}`);
            toast.success(`Merged · ${r.data.answers_moved} answers moved · +${r.data.score_added} score`);
            onChange?.();
        } catch (e) {
            toast.error(formatErr(e));
        }
    };

    const act = async (fn, msg) => {
        try {
            await fn();
            toast.success(msg);
            setOpen(false);
            onChange?.();
        } catch (e) { toast.error(formatErr(e)); }
    };

    const del = async () => {
        const answer = await promptDialog("Reason for deletion (optional)?", { title: "Delete content", confirmText: "delete", danger: true });
        if (answer === null) return;
        const reason = answer || "";
        act(() => api.delete(`/admin/content/${targetType}/${targetId}`, { data: { reason } }), "Deleted");
    };
    const restore = () => act(() => api.post(`/admin/content/${targetType}/${targetId}/restore`), "Restored");
    const lock = () => act(() => api.post(`/admin/content/${targetType}/${targetId}/lock`), isLocked ? "Unlocked" : "Locked");
    const pin = () => act(() => api.post(`/admin/content/${targetType}/${targetId}/pin`), isPinned ? "Unpinned" : "Pinned");

    const menu = open && createPortal(
        <div
            ref={menuRef}
            className="fixed z-[1000] min-w-[200px] bg-[#0b121f] border border-white/10 rounded-sm shadow-2xl py-1 text-xs"
            style={{ top: pos.top, left: pos.left }}
            onClick={(e) => e.stopPropagation()}
        >
            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReport?.(); setOpen(false); }} className="w-full text-left px-3 py-2 hover:bg-slate-800/60 text-slate-300 flex items-center gap-2" data-testid={`report-btn-${targetId}`}>
                <Flag size={12} /> Report
            </button>
            {isAdmin && (
                <>
                    <div className="h-px bg-slate-800 my-1" />
                    <div className="px-3 py-1 text-[9px] uppercase tracking-widest text-slate-600">// admin</div>
                    {canLockPin && (
                        <>
                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); pin(); }} className="w-full text-left px-3 py-2 hover:bg-slate-800/60 text-slate-300 flex items-center gap-2" data-testid={`pin-btn-${targetId}`}>
                                <PushPin size={12} weight={isPinned ? "fill" : "regular"} className={isPinned ? "text-amber-400" : ""} /> {isPinned ? "Unpin" : "Pin"}
                            </button>
                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); lock(); }} className="w-full text-left px-3 py-2 hover:bg-slate-800/60 text-slate-300 flex items-center gap-2" data-testid={`lock-btn-${targetId}`}>
                                {isLocked ? <LockOpen size={12} /> : <Lock size={12} />} {isLocked ? "Unlock" : "Lock"}
                            </button>
                        </>
                    )}
                    {canMerge && (
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); merge(); setOpen(false); }} className="w-full text-left px-3 py-2 hover:bg-slate-800/60 text-cyan-300 flex items-center gap-2" data-testid={`merge-btn-${targetId}`}>
                            <GitMerge size={12} /> Merge into…
                        </button>
                    )}
                    {isDeleted ? (
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); restore(); }} className="w-full text-left px-3 py-2 hover:bg-slate-800/60 text-emerald-400 flex items-center gap-2" data-testid={`restore-btn-${targetId}`}>
                            <ArrowCounterClockwise size={12} /> Restore
                        </button>
                    ) : (
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); del(); }} className="w-full text-left px-3 py-2 hover:bg-slate-800/60 text-rose-400 flex items-center gap-2" data-testid={`delete-btn-${targetId}`}>
                            <Trash size={12} /> Delete
                        </button>
                    )}
                </>
            )}
        </div>,
        document.body,
    );

    return (
        <>
            <button
                ref={btnRef}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
                className="p-1 text-slate-500 hover:text-slate-200 hover:bg-slate-800/50 rounded-sm"
                data-testid={`mod-menu-${targetId}`}
                aria-label="Menu"
            >
                <DotsThreeVertical size={16} weight="bold" />
            </button>
            {menu}
        </>
    );
}
